/**
 * Helpers de formatação para o módulo Tasks.
 * DB armazena segundos crus; UI quebra em d/h/min/s e omite componentes zero.
 */

const SEC = 1;
const MIN = 60;
const HOUR = 60 * 60;
const DAY = 24 * HOUR;

/**
 * Formata duration "flexível": prefere segundos canônicos (P1.12 Frente Q/F),
 * fallback pra hours legacy (`demands.estimated_duration_hours`,
 * pre-Frente-Q). Só pra leituras — escritas DEVEM usar `_seconds`.
 *
 * - Se `seconds` é número finito > 0, usa `seconds` direto.
 * - Senão, se `legacyHours` é número finito > 0, converte `_hours * 3600`.
 * - Senão, retorna "—".
 *
 * Regra T do plano P1.12 (sec 4.1): UIs em prod podem ler ainda demands
 * antigas que só têm `_hours`. Dual-read durante migração.
 */
export function formatDurationFlexible(
  seconds: number | null | undefined,
  legacyHours?: number | null | undefined,
): string {
  if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0) {
    return formatDurationHuman(seconds);
  }
  if (typeof legacyHours === 'number' && Number.isFinite(legacyHours) && legacyHours > 0) {
    return formatDurationHuman(legacyHours * 3600);
  }
  return '—';
}

/**
 * Formata segundos como string humana ("1h 3min 5s"), omitindo componentes zero.
 * Negativos preservam sinal: -65 → "−1min 5s".
 * null/undefined → "—".
 */
export function formatDurationHuman(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
  const sign = seconds < 0 ? '−' : '';
  let abs = Math.round(Math.abs(seconds));

  if (abs === 0) return '0s';

  const days = Math.floor(abs / DAY); abs -= days * DAY;
  const hours = Math.floor(abs / HOUR); abs -= hours * HOUR;
  const minutes = Math.floor(abs / MIN); abs -= minutes * MIN;
  const secs = abs / SEC;

  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}min`);
  if (secs) parts.push(`${secs}s`);

  return sign + parts.join(' ');
}

/**
 * Formata "atual / estimado" pra task em execução. Ex: "4s / 8s".
 * Se estimated for null, retorna só atual.
 */
export function formatDurationVsEta(
  actual: number | null | undefined,
  estimated: number | null | undefined,
): string {
  const a = formatDurationHuman(actual);
  if (estimated === null || estimated === undefined) return a;
  return `${a} / ${formatDurationHuman(estimated)}`;
}

/**
 * Calcula segundos decorridos desde started_at até agora (ou completed_at se done).
 * Retorna null se started_at é null.
 */
export function elapsedSeconds(
  startedAt: string | null | undefined,
  completedAt?: string | null | undefined,
): number | null {
  if (!startedAt) return null;
  const start = Date.parse(startedAt);
  if (Number.isNaN(start)) return null;
  const end = completedAt ? Date.parse(completedAt) : Date.now();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.round((end - start) / 1000));
}

/**
 * G4 F1 (D-G4-F): converte duration_ms (bigint canonical do backend pós G3 PR-P1)
 * em segundos pra display. Retorna null quando input é null/undefined.
 */
export function durationMsToSeconds(ms: number | null | undefined): number | null {
  if (ms === null || ms === undefined) return null;
  return Math.max(0, Math.floor(ms / 1000));
}

/**
 * P1.15 Z3 — Calcula delta terminal (atraso/folga) em SEGUNDOS arredondados.
 *
 * Backend canonical:
 *   - `tasks.duration_ms` (bigint, populado em estado terminal pelo writer)
 *   - `tasks.estimated_duration_seconds` (integer, populado pelo decomposer
 *     na criação da task)
 *
 * Frontend computa delta = `duration_ms - estimated_seconds * 1000`, normalizado
 * pra segundos (Math.round). Convenção de sinal:
 *   - `> 0` (positivo) → real DEMOROU MAIS que estimado (atraso, warning)
 *   - `< 0` (negativo) → real foi MAIS RÁPIDO que estimado (folga, success)
 *   - `=== 0` → bateu exato
 *
 * Retorna null quando qualquer input é null/undefined — não dá pra computar
 * delta sem ambos os lados. UI esconde a label nessa condição.
 *
 * Por que computar no frontend (e não consumir `delta_estimated_actual_seconds`
 * do backend): canonicalmente a coluna não existe em tasks; é derivada. Manter
 * lógica no front evita drift entre fórmulas e elimina campo redundante no DB.
 */
export function computeDeltaSeconds(
  durationMs: number | null | undefined,
  estimatedSeconds: number | null | undefined,
): number | null {
  if (durationMs === null || durationMs === undefined) return null;
  if (estimatedSeconds === null || estimatedSeconds === undefined) return null;
  const deltaMs = durationMs - estimatedSeconds * 1000;
  return Math.round(deltaMs / 1000);
}

/**
 * P1.15 Z3 — Formata delta como "+30s" (atraso) ou "−15s" (mais rápido).
 *   - Sinal "+" explícito quando positivo.
 *   - Reutiliza `formatDurationHuman` (que já preserva "−" unicode pra negativos).
 *   - null → "—".
 *
 * Ex:
 *   formatDeltaSeconds(30)   → "+30s"
 *   formatDeltaSeconds(-15)  → "−15s"
 *   formatDeltaSeconds(0)    → "0s"
 *   formatDeltaSeconds(null) → "—"
 */
export function formatDeltaSeconds(delta: number | null | undefined): string {
  if (delta === null || delta === undefined) return '—';
  const formatted = formatDurationHuman(delta);
  if (delta > 0) return `+${formatted}`;
  return formatted;
}

/**
 * "há 2min", "há 5h", "há 3d" — formato relativo curto. null → "—".
 * Threshold: <60s → "agora".
 */
export function relativeFromNow(iso: string | null | undefined, nowMs?: number): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const now = nowMs ?? Date.now();
  const diffSec = Math.round((now - t) / 1000);
  if (diffSec < 0) return formatDurationHuman(-diffSec) + ' a partir de agora';
  if (diffSec < 60) return 'agora';
  return 'há ' + formatDurationHuman(diffSec);
}

/**
 * Activity dot semantic baseado em age desde updated_at.
 * fresh < 5min, recent < 1h, stale ≥ 1h.
 */
export type ActivityFreshness = 'fresh' | 'recent' | 'stale';
export function activityFreshness(updatedAt: string | null | undefined, nowMs?: number): ActivityFreshness {
  if (!updatedAt) return 'stale';
  const t = Date.parse(updatedAt);
  if (Number.isNaN(t)) return 'stale';
  const ageSec = ((nowMs ?? Date.now()) - t) / 1000;
  if (ageSec < 5 * MIN) return 'fresh';
  if (ageSec < HOUR) return 'recent';
  return 'stale';
}

/**
 * Trunca UUID exibindo só os 8 primeiros chars com prefixo opcional.
 */
export function shortId(id: string | null | undefined, prefix = ''): string {
  if (!id) return '—';
  return prefix + id.slice(0, 8);
}

/**
 * Calcula proporção [0..1] de tempo decorrido vs estimado, capped em 1.
 * Útil pra barra de progresso em task running.
 */
export function progressRatio(actual: number | null, estimated: number | null): number | null {
  if (actual === null || estimated === null || estimated <= 0) return null;
  return Math.min(1, actual / estimated);
}
